import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer, Cell,
} from 'recharts';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sparkles } from 'lucide-react';
import { fmtInt, fmtNum } from '@/lib/format';

/**
 * Reusable Pareto (80/20) panel.
 * `data` = backend pareto result { items[], total, count, vital_count, vital_share, vital_value_share, metric }
 */
export const ParetoPanel = ({
  data, labelKey = 'key', entityLabel = 'éléments', unitLabel = 'résultats',
  valueFmt = (v) => fmtInt(v), subLabelFn, topN = 25, onSelect,
}) => {
  if (!data || !data.count) {
    return <p className="py-10 text-center text-sm text-muted-foreground">Aucune donnée pour cette analyse.</p>;
  }
  const items = data.items || [];
  const vital = data.vital_count || 0;
  const chartData = items.slice(0, topN).map((x, i) => ({
    name: x[labelKey],
    value: x.value,
    cum: Math.round((x.cum_share || 0) * 1000) / 10,
    vital: i < vital,
  }));
  const vitalItems = items.slice(0, vital);

  return (
    <div className="space-y-4" data-testid="pareto-panel">
      {/* Headline insight */}
      <div className="flex flex-wrap items-center gap-4 rounded-2xl border bg-gradient-to-br from-[color:var(--tenant-accent-100)]/60 to-transparent p-4">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl accent-bg-100">
          <Sparkles className="h-5 w-5 accent-text" />
        </div>
        <div className="min-w-0">
          <p className="font-display text-lg font-semibold text-foreground">
            {fmtInt(vital)} {entityLabel} <span className="accent-text">({fmtNum(data.vital_share * 100, 0)}%)</span> génèrent {fmtNum(data.vital_value_share * 100, 0)}% des {unitLabel}
          </p>
          <p className="text-sm text-muted-foreground">
            Sur {fmtInt(data.count)} {entityLabel} au total · concentrez vos efforts sur ce noyau à forte valeur.
          </p>
        </div>
      </div>

      {/* Pareto chart */}
      <div style={{ height: 320 }}>
        <ResponsiveContainer width="100%" height="100%" minWidth={0}>
          <ComposedChart data={chartData} margin={{ top: 8, right: 8, bottom: 4, left: -8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(11,19,32,0.06)" vertical={false} />
            <XAxis dataKey="name" tick={false} axisLine={false} tickLine={false}
              height={8} label={{ value: `${entityLabel} classés par valeur décroissante`, position: 'insideBottom', offset: -2, style: { fontSize: 10, fill: 'hsl(var(--muted-foreground))' } }} />
            <YAxis yAxisId="left" tickLine={false} axisLine={false} width={44} tick={{ fontSize: 11 }} />
            <YAxis yAxisId="right" orientation="right" domain={[0, 100]} width={38}
              tickLine={false} axisLine={false} tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
            <Tooltip
              cursor={{ fill: 'rgba(11,19,32,0.04)' }}
              content={({ active, payload }) => {
                if (!active || !payload || !payload.length) return null;
                const p = payload[0].payload;
                return (
                  <div className="rounded-lg border bg-popover px-3 py-2 text-xs shadow-md">
                    <p className="font-medium text-foreground break-words">{p.name}</p>
                    <p className="text-muted-foreground">{valueFmt(p.value)} {unitLabel}</p>
                    <p className="text-muted-foreground">Cumul : {fmtNum(p.cum, 1)}%</p>
                  </div>
                );
              }} />
            <ReferenceLine yAxisId="right" y={80} strokeDasharray="4 4" stroke="var(--tenant-accent-500)"
              label={{ value: '80%', position: 'right', style: { fontSize: 10, fill: 'var(--tenant-accent-600, #0b1320)' } }} />
            <Bar yAxisId="left" dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={24}>
              {chartData.map((e, i) => (
                <Cell key={i} fill={e.vital ? 'var(--tenant-accent-500)' : 'var(--tenant-accent-100)'} />
              ))}
            </Bar>
            <Line yAxisId="right" type="monotone" dataKey="cum" stroke="#0b1320" strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Vital few table */}
      <div className="rounded-xl border">
        <div className="border-b px-4 py-2 text-xs font-medium text-muted-foreground">
          Le noyau vital ({fmtInt(vital)} {entityLabel})
        </div>
        <div className="max-h-72 overflow-y-auto">
          <Table>
            <TableHeader><TableRow className="hover:bg-transparent">
              <TableHead className="w-10 pl-4">#</TableHead>
              <TableHead>{entityLabel === 'spots' ? 'Spot' : 'Recruteur'}</TableHead>
              <TableHead className="text-right">{unitLabel}</TableHead>
              <TableHead className="text-right">Part</TableHead>
              <TableHead className="text-right">Cumul</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {vitalItems.map((x, i) => (
                <TableRow key={x[labelKey]} className={onSelect ? 'cursor-pointer' : ''}
                  onClick={onSelect ? () => onSelect(x) : undefined} data-testid="pareto-row">
                  <TableCell className="pl-4 text-sm text-muted-foreground tabular">{i + 1}</TableCell>
                  <TableCell className="break-words text-sm font-medium">
                    {x[labelKey]}
                    {subLabelFn && <span className="mt-0.5 block text-[11px] font-normal text-muted-foreground">{subLabelFn(x)}</span>}
                  </TableCell>
                  <TableCell className="text-right tabular">{valueFmt(x.value)}</TableCell>
                  <TableCell className="text-right tabular text-muted-foreground">{fmtNum((x.share || 0) * 100, 1)}%</TableCell>
                  <TableCell className="text-right tabular font-medium">{fmtNum((x.cum_share || 0) * 100, 1)}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};
